import { mockReferralDB } from './mockDb';
import type { Referral, ReferralFormData, PublicReferralFormData } from '../types';
import { 
  evaluateCommissionEligibility, 
  calculateCommissionAmount, 
  isDuplicateReferral, 
  checkRateLimit 
} from '../utils/referralUtils';

export const referralService = {
  async getAll(): Promise<Referral[]> {
    await new Promise(resolve => setTimeout(resolve, 100));
    return mockReferralDB.getReferrals();
  },

  async getById(id: string): Promise<Referral> {
    await new Promise(resolve => setTimeout(resolve, 100));
    const referral = mockReferralDB.getReferral(id);
    if (!referral) throw new Error('Referral not found');
    return referral;
  },

  async getByType(type: 'in' | 'out'): Promise<Referral[]> {
    await new Promise(resolve => setTimeout(resolve, 100));
    return mockReferralDB.getReferralsByType(type);
  },

  async getByReferrer(referrerId: string): Promise<Referral[]> {
    await new Promise(resolve => setTimeout(resolve, 100));
    return mockReferralDB.getReferralsByReferrer(referrerId);
  },

  async create(data: ReferralFormData): Promise<Referral> {
    await new Promise(resolve => setTimeout(resolve, 200));
    
    const policy = mockReferralDB.getPolicy();
    if (policy?.enableDuplicateDetection && data.patientPhone) {
      const existingReferrals = mockReferralDB.getReferrals();
      const isDuplicate = isDuplicateReferral(
        { patientPhone: data.patientPhone, dateCreated: new Date().toISOString() },
        existingReferrals
      );
      
      if (isDuplicate) {
        const referral = mockReferralDB.createReferral({
          ...data,
          patientId: data.patientId || 'temp-patient-id',
          status: 'pending',
          duplicate: true,
        });
        return referral;
      }
    }

    return mockReferralDB.createReferral({
      ...data,
      patientId: data.patientId || 'temp-patient-id',
      status: 'pending',
    });
  },

  async createPublicReferral(code: string, data: PublicReferralFormData): Promise<Referral> {
    await new Promise(resolve => setTimeout(resolve, 200));
    
    const resolved = mockReferralDB.resolveReferrerByCode(code);
    if (!resolved) {
      throw new Error('Invalid referral code');
    }

    const policy = mockReferralDB.getPolicy();
    if (policy) {
      const existingReferrals = mockReferralDB.getReferrals();
      const rateCheck = checkRateLimit(resolved.entity.id, existingReferrals, policy.publicReferralRateLimit);
      
      if (!rateCheck.allowed) {
        throw new Error(`Rate limit exceeded. ${rateCheck.count}/${policy.publicReferralRateLimit} referrals in the last hour.`);
      }
    }

    if (policy?.enableDuplicateDetection) {
      const existingReferrals = mockReferralDB.getReferrals();
      const isDuplicate = isDuplicateReferral(
        { patientPhone: data.patientPhone, dateCreated: new Date().toISOString() },
        existingReferrals
      );
      
      if (isDuplicate) {
        const referral = mockReferralDB.createReferral({
          type: 'in',
          patientId: 'temp-patient-id',
          patientName: data.patientName,
          patientPhone: data.patientPhone,
          consentGiven: data.consentGiven,
          source: {
            kind: resolved.kind as any,
            id: resolved.entity.id,
            code,
          },
          toSpecialty: data.toSpecialty,
          reason: data.reason,
          status: 'pending',
          duplicate: true,
        });
        return referral;
      }
    }

    return mockReferralDB.createReferral({
      type: 'in',
      patientId: 'temp-patient-id',
      patientName: data.patientName,
      patientPhone: data.patientPhone,
      consentGiven: data.consentGiven,
      source: {
        kind: resolved.kind as any,
        id: resolved.entity.id,
        code,
      },
      toSpecialty: data.toSpecialty,
      reason: data.reason,
      status: 'pending',
    });
  },

  async update(id: string, data: Partial<ReferralFormData>): Promise<Referral> {
    await new Promise(resolve => setTimeout(resolve, 200));
    const updated = mockReferralDB.updateReferral(id, data);
    if (!updated) throw new Error('Referral not found');
    return updated;
  },

  async updateStatus(id: string, status: Referral['status']): Promise<Referral> {
    await new Promise(resolve => setTimeout(resolve, 200));
    const referral = mockReferralDB.getReferral(id);
    if (!referral) throw new Error('Referral not found');

    const updated = mockReferralDB.updateReferral(id, { status });
    if (!updated) throw new Error('Failed to update referral');

    if (status === 'completed') {
      await this.processCommission(updated);
    }

    return updated;
  },

  async processCommission(referral: Referral): Promise<void> {
    const policy = mockReferralDB.getPolicy();
    if (!policy) return;

    const eligibility = evaluateCommissionEligibility(referral, policy);
    if (!eligibility.eligible) {
      console.log(`Commission blocked: ${eligibility.reason}`);
      return;
    }

    const rule = mockReferralDB.getCommissionRuleByReferrer(referral.source.id);
    if (!rule) {
      console.log('No commission rule found for referrer');
      return;
    }

    const amount = calculateCommissionAmount(rule, referral.estimatedAmount || 0);

    mockReferralDB.createCommissionEntry({
      referralId: referral.id,
      referrer: referral.source,
      calculatedAmount: amount,
      payoutStatus: 'unpaid',
    });
  },

  async resolveReferrerByCode(code: string): Promise<{ kind: string; entity: any } | null> {
    await new Promise(resolve => setTimeout(resolve, 100));
    return mockReferralDB.resolveReferrerByCode(code);
  },
};
